import { FC, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'next-i18next'
import { CSSTransition } from 'react-transition-group'
import { useRafLoop } from 'react-use'
import classNames from 'classnames'

import { poppins } from '@/components/common/fonts'

import { Area } from './styles'

type ResultAreaProps = {
  imageSrc?: string
  /**
   * 0 ~ 100
   */
  progress?: number

  isGenerated?: boolean
  isGenerating?: boolean

  className?: string
}

/**
 * 프로그레스 시간: 1분\
 * unit: ms
 */
const PROGRESS_DURATION = 1000 * 60
/**
 * 페이드 딜레이 1s\
 * unit: ms
 */
const FADE_DELAY = 1000
/**
 * 페이드 시간: 0.2s\
 * unit: ms
 */
const FADE_DURATION = 200

export const ResultArea: FC<ResultAreaProps> = ({
  imageSrc,

  isGenerated,
  isGenerating,

  className,
}) => {
  const { t } = useTranslation('common')

  return (
    <ResultAreaContainer className={className}>
      <ResultPreview>
        {isGenerated && !!imageSrc ? (
          <ResultPreviewImage src={imageSrc} />
        ) : (
          <ResultPreviewEmpty>{t('Generate an image!')}</ResultPreviewEmpty>
        )}
      </ResultPreview>

      <CSSTransition
        in={isGenerating}
        timeout={FADE_DURATION + FADE_DELAY}
        classNames="fade"
        mountOnEnter
        unmountOnExit
        css={css`
          &.fade-exit {
            opacity: 1;
          }

          &.fade-exit-active {
            opacity: 0;
            transition: opacity ${FADE_DURATION}ms ${FADE_DELAY}ms;
          }
        `}
      >
        {/* @ts-expect-error */}
        <ResultProgress isGenerating={isGenerating} />
      </CSSTransition>
    </ResultAreaContainer>
  )
}

const ResultAreaContainer = styled(Area)`
  grid-column: 2;
  grid-row: 1;

  border-bottom: 1px solid #f3f3f3;

  @media (max-width: ${({ theme }) => theme.screenMD}px) {
    grid-column: auto;
    grid-row: auto;
  }
`

const ResultPreview = styled.div`
  display: flex;
  justify-content: stretch;

  margin-bottom: 13px;

  aspect-ratio: 342 / 264;
`

const ResultPreviewImage = styled.div<{
  src: string
}>`
  flex: 1;

  background-image: url(${({ src }) => src});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
`

const ResultPreviewEmpty = styled.div`
  flex: 1;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: #eef6ff;

  font-family: ${poppins.string};
  font-size: 16px;
  line-height: 24px;
  color: #4276ee;
`

const ResultProgress: FC<{
  isGenerating?: boolean
  className?: string
}> = ({ isGenerating, className }) => {
  const { t } = useTranslation('common')

  const [startedAt, setStartedAt] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState<number | null>(null)

  const progress = useMemo(() => {
    if (!isGenerating) {
      return 100
    }

    if (!startedAt) {
      return 0
    }

    if (!currentTime) {
      return 0
    }

    const elapsed = currentTime - startedAt

    if (elapsed > PROGRESS_DURATION) {
      return 100
    }

    return (elapsed / PROGRESS_DURATION) * 100
  }, [isGenerating, startedAt, currentTime])

  const [stop, start] = useRafLoop(() => {
    setCurrentTime(Date.now())
  }, false)

  useEffect(() => {
    if (isGenerating) {
      setStartedAt(Date.now())
      start()
    } else {
      stop()
    }
  }, [isGenerating, start, stop])

  const progressText = `${Math.floor(progress)}%`

  return (
    <ResultProgressContainer className={className}>
      <ProgressDescription>
        <span className="title">{t('In Progress')}</span>
        <span className="progress">{progressText}</span>
      </ProgressDescription>

      <ProgressBar
        className={classNames({
          completed: !isGenerating,
        })}
      >
        <ProgressBarInner
          style={{
            width: `${progress}%`,
            transition: isGenerating ? undefined : 'width 200ms',
          }}
        />
      </ProgressBar>
    </ResultProgressContainer>
  )
}

const ResultProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

/**
 * - .title: 제목
 * - .progress: 퍼센티지
 */
const ProgressDescription = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  font-family: ${poppins.string};

  .title {
    font-size: 16px;
    line-height: 12px;
    font-weight: 500;
    color: #000000;
  }

  .progress {
    font-size: 18px;
    line-height: 16px;
    font-weight: 600;
    color: #3075f6;
  }
`

const ProgressBar = styled.div`
  position: relative;

  width: 100%;
  height: 8px;

  border-radius: 9999px;
  background-color: #f1f2f480;
`

const ProgressBarInner = styled.div`
  position: absolute;
  inset: 0;

  background-image: linear-gradient(
    89.98deg,
    #17ead9 1.15%,
    #6078ea 67.72%,
    #3075f6 128.52%
  );

  border-radius: 9999px;
  min-width: 8px;
`
